<template>
	<section class="course insicht insicht--fade-up">
		<div class="course__list">
      <a ref="startAnchor" class="anchor"/>
			<h2
				class="course__title"
				v-sanitize="$t(`course.title.${eventType}`)"
			/>

      <template v-if="!selectedEvent">
        <template v-if="showRegionSelection">
          <div class="course__row">
            <div class="course__row-items">
              <FormInputSelect
                v-model="selectedRegionCode"
                :options="regionOptions"
                :label="$t('course.regionLabel')"
                :disabled="loading"
              />
            </div>
          </div>
        </template>

        <div
          v-if="isBooking && events.length"
          class="course__row"
        >
          <div class="course__row-items">
            <AvailabilityCalendar
              v-model="selectedDate"
              class="course__calendar"
              :min-date="availableMinDate"
              :max-date="availableMaxDate"
              :availabilities="monthAvailabilities"
              hide-legend
              @change:month="onMonthChange"
              required
            />
          </div>
        </div>

        <template v-if="!showRegionSelection || selectedRegionCode">
          <div
            class="course__row"
            v-for="region in eventsByRegionsFiltered"
            :key="region.code"
          >
            <h3
              v-if="!showRegionSelection && eventsByRegions[0].code !== 'none' && (region.visibleEvents.length || (isBooking && selectedDate))"
              class="course__row-title"
              v-sanitize="region.name"
            />

            <div
              v-if="region.visibleEvents.length"
              class="course__row-items"
            >
              <CourseListItem
                v-for="event in region.visibleEvents"
                :key="event.id"
                :event="event"
                :button-text="$t('course.chooseButton')"
                @course:select="onSelectDate"
                @course:info="onInfoModalOpen"
              />
            </div>
            <div
              v-else-if="isBooking && selectedDate"
              class="course__row-empty"
              v-html="$t('course.noDates')"
            />
          </div>
        </template>

        <div
          class="course__row"
          v-if="!events.length"
        >
          <h3
            class="course__row-title"
            v-sanitize="$t('course.noDates')"
          />
        </div>
      </template>
      <template v-else>
        <div class="course__row">
          <div class="course__row-items">
            <CourseListItem
              :event="selectedEvent"
              @course:info="onInfoModalOpen"
            />
          </div>
        </div>
        <Button
          :text="$t('course.resetButton')"
          level="tertiary"
          arrow
          @click="onResetSelection"
        />
      </template>
		</div>

		<div
			class="checkout-form__container"
			v-if="form.eventId"
		>
      <a ref="formAnchor" class="anchor"/>
			<h2
				class="course__title"
				v-sanitize="$t('course.formTitle')"
			/>

			<GeneralMessage
				ref="message"
				v-if="response.message"
				:message="response.message"
				:type="response.type"
				stick
			/>

			<ValidationObserver
				ref="form"
				v-slot="{ invalid, errors }"
				tag="form"
				class="checkout-form__form-wrapper"
				novalidate
				@submit.prevent="onSubmit"
			>

				<div
          v-if="!isBooking"
          class="input-row"
        >
					<FormInputSelect
						v-model="form.participants"
						:options="participants"
						:label="$t('course.participantsLabel')"
						:disabled="loading"
					/>
				</div>

				<ValidationObserver
					tag="section"
					ref="address"
					vid="address"
					v-slot="{ invalid }"
				>
					<div class="input-row">
						<ValidationProvider
							class="input-row__wrapper"
							name="firstname"
							rules="required"
							v-slot="{ errors, invalid, validated }"
							tag="div"
							:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
						>
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.firstname')"
								:label="$t('forms.labels.firstname')"
								name="firstname"
								v-model="form.firstName"
								:error="errors.length > 0"
								:disabled="loading"
								required
							/>
							<span
								class="input-error"
								v-if="invalid && validated"
								v-sanitize="errors[0]"
							/>
						</ValidationProvider>
						<ValidationProvider
							class="input-row__wrapper"
							name="lastname"
							rules="required"
							v-slot="{ errors, invalid, validated }"
							tag="div"
							:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
						>
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.lastname')"
								:label="$t('forms.labels.lastname')"
								name="lastname"
								v-model="form.lastName"
								:error="errors.length > 0"
								:disabled="loading"
								required
							/>
							<span
								class="input-error"
								v-if="invalid && validated"
								v-sanitize="errors[0]"
							/>
						</ValidationProvider>
					</div>

					<div class="input-row">
						<FormInput
							type="text"
							:placeholder="$t('forms.labels.company')"
							:label="$t('forms.labels.company')"
							:labelOption="$t('forms.labels.optional')"
							name="company"
							v-model="form.company"
							:required="false"
							:disabled="loading"
						/>
					</div>

					<div class="input-row">

						<ValidationObserver
							class="input-row__wrapper"
							tag="div"
							v-slot="{ validated, invalid, errors }"
						>
							<div class="input-group">
								<ValidationProvider
									class="input-group__span-9"
									name="street"
									rules="required"
									v-slot="{ errors, invalid }"
									tag="div"
									:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
								>
									<FormInput
										type="text"
										:placeholder="$t('forms.labels.street')"
										:label="$t('forms.labels.street')"
										name="street"
										v-model="form.street"
										:error="errors.length > 0"
										:disabled="loading"
										required
									/>
								</ValidationProvider>
								<ValidationProvider
										class="input-group__span-3"
										name="streetNumber"
										rules="required"
										v-slot="{ errors, invalid }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
									>
									<FormInput
										type="text"
										:placeholder="$t('forms.labels.number')"
										:label="$t('forms.labels.number')"
										name="number"
										v-model="form.streetNumber"
										:error="errors.length > 0"
										:disabled="loading"
										required
									/>
								</ValidationProvider>
							</div>
							<div
								class="input-error input-group__span-12"
								v-if="invalid && validated"
								v-sanitize="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
							/>
						</ValidationObserver>

						<ValidationObserver
							class="input-row__wrapper"
							tag="div"
							ref="zipCity"
							v-slot="{ dirty, validated, invalid, errors }"
						>
							<div class="input-group">
								<ValidationProvider
									class="input-group__span-4 input-group__span-xxl-3"
									name="zip"
									rules="required|digits:4"
									v-slot="{ errors, invalid }"
									tag="div"
									:customMessages="{ required: $t('forms.errors.fieldEmpty'), digits: $t('forms.errors.zip') }"
								>
									<FormInput
										ref="courseZip"
										type="text"
										@keydown="onZipKeydown"
										@input="onZipInput"
										:placeholder="$t('forms.labels.zip')"
										:label="$t('forms.labels.zip')"
										name="zip"
										v-model="form.postcode"
										:minlength="4"
										:maxlength="4"
										inputmode="decimal"
										pattern="[0-9]{4}"
										:error="errors.length > 0"
										:disabled="loading"
										required
									/>
								</ValidationProvider>
								<ValidationProvider
										class="input-group__span-8 input-group__span-xxl-9"
										name="city"
										rules="required"
										v-slot="{ errors, invalid }"
										tag="div"
										:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
									>
									<FormInput
										type="text"
										:placeholder="$t('forms.labels.city')"
										:label="$t('forms.labels.city')"
										name="city"
										v-model="form.city"
										:error="errors.length > 0"
										:disabled="loading"
										required
									/>
								</ValidationProvider>
							</div>
							<div
								class="input-error input-group__span-12"
								v-if="invalid && (validated || dirty)"
								v-sanitize="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
							/>
						</ValidationObserver>
					</div>

					<div class="input-row">
						<ValidationProvider
							class="input-row__wrapper"
							name="phone"
							rules="required|phone"
							v-slot="{ errors, invalid, validated }"
							tag="div"
							:customMessages="{ required: $t('forms.errors.fieldEmpty'), phone: $t('forms.errors.phone') }"
						>
							<FormInput
								type="tel"
								:placeholder="$t('forms.labels.phone')"
								:label="$t('forms.labels.phone')"
								name="phone"
								v-model="form.phoneNumber"
								:error="errors.length > 0"
								:disabled="loading"
								required
							/>
							<span
								class="input-error"
								v-if="invalid && validated"
								v-sanitize="errors[0]"
							/>
							<span class="additional-info" v-sanitize="$t('course.phoneHint')"/>
						</ValidationProvider>

						<ValidationProvider
							class="input-row__wrapper"
							name="email"
							rules="required|email"
							v-slot="{ errors, invalid, validated }"
							tag="div"
							:customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
						>
							<FormInput
								type="email"
								name="email"
								autocomplete="email"
								:placeholder="$t('forms.labels.email')"
								:label="$t('forms.labels.email')"
								v-model="form.email"
								:error="errors.length > 0"
								:disabled="loading"
								required
							/>
							<span
								class="input-error"
								v-if="invalid && validated"
								v-sanitize="errors[0]"
							/>
							<span class="additional-info" v-sanitize="$t('course.emailHint')"/>
						</ValidationProvider>
					</div>

				</ValidationObserver>


				<div class="input-row">
					<div class="input-row__wrapper">
						<FormInputTextarea
							:placeholder="$t('forms.labels.notes')"
							:label="$t('forms.labels.notes')"
							:labelOption="$t('forms.labels.optional')"
							v-model="form.notes"
							:disabled="loading"
						/>
					</div>
				</div>

				<template
					v-if="!isBooking && form.participants > 1"
				>
					<h3
						class="course__subtitle"
						v-sanitize="$t('course.participantsTitle')"
					/>
					<template
						v-for="participant in form.participants - 1"
					>
						<label
							class="input-row-label"
							v-sanitize="$t('course.participantRowLabel', { number: participant })"
						/>
						<div
							class="input-row"
						>
							<ValidationProvider
								class="input-row__wrapper"
								:name="`firstname-${participant}`"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.firstname')"
									:label="$t('forms.labels.firstname')"
									name="firstname"
									v-model="participantFirstNames[participant - 1]"
									:error="errors.length > 0"
									:disabled="loading"
									required
								/>
								<span
									class="input-error"
									v-if="invalid && validated"
									v-sanitize="errors[0]"
								/>
							</ValidationProvider>
							<ValidationProvider
								class="input-row__wrapper"
								:name="`lastname-${participant}`"
								rules="required"
								v-slot="{ errors, invalid, validated }"
								tag="div"
								:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
							>
								<FormInput
									type="text"
									:placeholder="$t('forms.labels.lastname')"
									:label="$t('forms.labels.lastname')"
									name="lastname"
									v-model="participantLastNames[participant - 1]"
									:error="errors.length > 0"
									:disabled="loading"
									required
								/>
								<span
									class="input-error"
									v-if="invalid && validated"
									v-sanitize="errors[0]"
								/>
							</ValidationProvider>
						</div>
					</template>
				</template>

				<FormInput
					ref="captcha"
					type="text"
					:placeholder="$t('forms.labels.addressDetails')"
					:label="$t('forms.labels.addressDetails')"
					:name="captcha.id"
					:id="captcha.id"
					v-model="captcha.value"
					required
					v-show="false"
				/>

				<ValidationProvider
					v-if="termsPage"
					:rules="{ required: { allowFalse: false } }"
					name="checkTerms"
					tag="div"
					v-slot="{ invalid, errors, validated }"
					:customMessages="{ required: $t('course.errorCancellationTerms') }"
				>
					<FormInputCheckbox
						name="terms"
						value="checkTerms"
						v-model="form.checkTerms"
						required
						immediate
					>
						<span v-sanitize="$t('course.cancellationTermsLabel', { url: termsPage })"/>
					</FormInputCheckbox>
					<span
						class="input-error"
						v-if="invalid && validated"
						v-sanitize="errors[0]"
					/>
				</ValidationProvider>

				<Button
					type="submit"
					level="primary"
					:text="$t('course.submitButton')"
					:disabled="loading"
				/>
			</ValidationObserver>
		</div>
    <Modal
			ref="infoModal"
      narrow
      @close="onInfoModalClose"
		>
      <CourseListItem
        v-if="infoModalEvent"
        :event="infoModalEvent"
        :button-text="form.eventId ? '' : $t('course.chooseButton')"
        show-description
        vertical
        @course:select="onSelectDate"
      />
		</Modal>
	</section>
</template>

<script>
import {mapGetters} from 'vuex'
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputTextarea from "@/components/FormInputTextarea"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputSelect from "@/components/FormInputSelectCustom"
import FormInputRadio from '@/components/FormInputRadio'
import GenericMixin from '@/mixins/GenericVueMixin'
import MixinCourse from '@/mixins/MixinCourse'
import Button from '@/components/Button'
import Loader from '@/components/Loader'
import Modal from '@/components/Modal'
import GeneralMessage from '@/components/GeneralMessage'
import scrrroll from 'scrrroll';
import CourseListItem from '@/components/CourseListItem.vue'
import { getUrlParams } from '@/utils/urlParams'
import {eventhub, EVENT_ACCOUNT_ISLOGGEDIN} from '@/store/eventhub'
import AvailabilityCalendar from '@/components/AvailabilityCalendar.vue'
import { switchDateFormat } from '@/utils/formatDate'

export default {
	mixins: [
		GenericMixin,
    MixinCourse,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputTextarea,
		FormInputCheckbox,
		FormInputSelect,
		FormInputRadio,
		Button,
		Loader,
		Modal,
		GeneralMessage,
    CourseListItem,
    AvailabilityCalendar,
	},
	data() {
		return {
			captcha: {
				id: this.captchaId(),
				value: '',
			},
			form: {
				eventId: '',
				participants: 1,
				company: '',
				firstName: '',
				lastName: '',
				street: '',
				streetNumber: '',
				postcode: '',
				city: '',
				phoneNumber: '',
				email: '',
				participantNames: '',
				notes: '',
				checkTerms: false,
			},
			participantFirstNames: [],
			participantLastNames: [],
			response: {
				message: '',
				type: '',
			},
      maxEventsWithoutRegion: 4,
      selectedRegionCode: null,
      infoModalEvent: null,
      selectedDate: null,
      currentMonth: ((new Date()).getMonth() + 1).toString().padStart(2, 0),
			currentYear: ((new Date()).getFullYear()).toString(),
			eventType: this.$root.options.eventType,
			termsPage: this.$root.options.termsPage,
		}
	},
	computed: {
		...mapGetters('loader', [
			'isLoading',
		]),
		...mapGetters('courses', [
			'courseDates',
		]),
    ...mapGetters('account', [
			'isLoggedIn',
			'accountLastAddress',
			'accountUserDetail',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
    events() {
			return this.courseDates
        .reduce((a, l) => {
          return [
            ...a,
            ...l.events
              .filter(event => event.free)
              .map(event => {
                return {
                  ...event,
                  location: l.location,
                  visible: this.isBooking ? this.selectedDate && event.date === switchDateFormat(this.selectedDate, '-') : true
                }
              })
          ]
        }, [])
        .sort((a, b) => (new Date(a.date)) - (new Date(b.date)) || a.fromTime.localeCompare(b.fromTime))
        .filter(event => {
          if (this.isEvent || this.isBooking) {
            return event?.location?.brand === this.brand
          }

          return true
        })
		},
    eventsByRegions() {
      const eventsWithRegion = this.events.filter(event => event.location.region)
			const eventsWithoutRegion = this.events.filter(event => !event.location.region)

      let regions = []

      if (eventsWithRegion.length) {
        regions = eventsWithRegion
          .reduce((regions, event) => {
            const { region: code } = event.location
            let region = regions.find(rr => rr.code === code)
            if (!region) {
              region = {
                code,
                name: this.$t(`orderbox.regions.${this.formatRegionCode(code)}`),
                events: []
              }
              regions.push(region)
            }
            region.events.push(event)
            return regions
          }, [])
          .sort((a, b) => a.code.localeCompare(b.code))
      }

      if (eventsWithoutRegion.length) {
        regions.push({
					code: 'none',
          name: this.$t('orderbox.regions.none'),
					events: eventsWithoutRegion
				})
      }
      return regions.map(region => ({ ...region, visibleEvents: region.events.filter(event => event.visible)}))
    },
    eventsByRegionsFiltered() {
      return this.eventsByRegions
        .filter(region => this.selectedRegionCode ? region.code === this.selectedRegionCode : true)
    },
    regionOptions() {
      return this.eventsByRegions.map(region => ({
        value: region.code,
        label: region.name
      }))
    },
    showRegionSelection() {
      return this.events.length > this.maxEventsWithoutRegion && this.eventsByRegions.length > 1
    },
		selectedEvent() {
			return this.events.find(c => c.id == this.form.eventId);
		},
    availableEventDates() {
      return Array.from(new Set(this.events.map(event => event.date)))
    },
    availableMinDate() {
      return !!this.availableEventDates.length ? switchDateFormat(this.availableEventDates.at(0), '.') : undefined
    },
    availableMaxDate() {
      return !!this.availableEventDates.length ? switchDateFormat(this.availableEventDates.at(-1), '.') : undefined
    },
    monthAvailabilities() {
      return this.eventsByRegionsFiltered
        .reduce((p, region) => {
          p.push(...region.events.filter(event => event.date.startsWith(`${this.currentYear}-${this.currentMonth}`)))
          return p
        }, [])
        .map(event => {
          return {
            date: switchDateFormat(event.date, '.'),
            is_available: true,
            reason: "open"
          }
        })
    },
		maximumParticipants() {
			const max = 8;
			const free = this.selectedEvent?.free || max;

			return Math.min(max, free);
		},
		participants() {
			const participants = [];
			for (let i = 1; i <= this.maximumParticipants; i++) {
				participants.push({
					value: i,
					label: i,
				});
			}
			return participants;
		},
		totalText() {
			if (!this.selectedEvent) {
				return '';
			}
			return `${this.form.participants}× ${this.formatDate(this.selectedEvent.date)} ${this.formatTime(this.selectedEvent)}`
		},
		totalSubtext() {
			if (!this.selectedEvent) {
				return '';
			}
			return `${this.selectedEvent.location.name}, ${this.selectedEvent.location.brand}`
		},
		totalValue() {
			if (!this.selectedEvent) {
				return 0
			}
			return this.form.participants * this.selectedEvent.price
		},
	},
	watch: {
		'form.participants'(val) {
			this.participantFirstNames.splice(val - 1);
			this.participantLastNames.splice(val - 1);
		},
    isLoggedIn(val) {
      if (val) {
        this.prefillAddress()
      }
    }
	},
	methods: {
    async onMonthChange({ month = this.currentMonth, year = this.currentYear } = {}) {
			this.currentMonth = month.toString().padStart(2, 0)
			this.currentYear = year.toString()
		},
		async onSelectDate(course) {
      this.$refs.infoModal?.close()
			this.form.participants = Math.min(course.free, this.form.participants)
			this.form.eventId = course.id

      await this.$nextTick()
      const start = this.$refs.startAnchor

      if (start) {
        scrrroll.to(start, 50)
      }
		},
    async onResetSelection() {
      this.form.eventId = null

      await this.$nextTick()
      const start = this.$refs.startAnchor

      if (start) {
        scrrroll.to(start, 50)
      }
    },
    onInfoModalOpen(event) {
      this.infoModalEvent = event
      this.$refs.infoModal?.open();
    },
    onInfoModalClose() {
      this.infoModalEvent = null
    },
		onZipKeydown(e) {
			if (!e.metaKey && !e.ctrlKey && e.key && e.key.length <= 1 && !/[0-9]/.test(e.key)) {
				e.preventDefault()
			}
		},
		onZipInput(value) {
			if (/[^\d]/.test(value)) {
				Vue.set(this.form, 'postcode', value.replace(/[^\d]/ig,'').substring(0,4));
				this.$refs.courseZip.$refs.input.value = this.form.postcode;
			}
		},
		async onSubmit() {
			this.loading = true;
			const valid = await this.$refs.form.validate();

			if (this.captcha.value || this.$refs.captcha.$refs.input.value) {
				this.loading = false;
				return;
			}

			if (valid) {
				if (this.form.participants > 1) {
					this.form.participantNames = this.participantFirstNames
						.reduce((names, firstName, index) => {
							const lastName = this.participantLastNames[index];
							const name = [];

							if (firstName) {
								name.push(firstName);
							}

							if (lastName) {
								name.push(lastName);
							}

							if (name.length) {
								names.push(name.join(' '));
							}


							return names;
						}, [])
					.join(', ');
				}

				try {
					const response = await this.$store.dispatch('courses/courseRegistration', {...this.form});
				}
				catch(err) {
          if (err.isMaintenanceError) {
            this.onMaintenanceMode({ callback: () => this.onSubmit(), retry: true })
          } else {
            if (err.response && err.response.data.message) {
              this.response.message = err.response.data.errors && Object.values(err.response.data.errors).length
                ? Object.values(err.response.data.errors).flat().join()
                : err.response.data.message
            } else {
              this.response.message = this.$t('forms.errors.generic');
            }
            this.response.type = 'error';
            this.$nextTick(() => {
              this.$refs.message.scrollTo();
            });
          }
					this.loading = false;
				}

			} else {
				await this.$nextTick();
				const inputError = this.$refs.form.$el.querySelector('.input-error');

				if (inputError) {
					scrrroll.to(inputError.parentNode)
				}
				this.loading = false
			}
		},
		resetForm() {
			this.$refs.form.reset();
			this.form.participants = 1;
			this.form.company = '';
			this.form.firstName = '';
			this.form.lastName = '';
			this.form.street = '';
			this.form.streetNumber = '';
			this.form.postcode = '';
			this.form.city = '';
			this.form.phoneNumber = '';
			this.form.email = '';
			this.form.participantNames = '';
			this.form.notes = '';
			this.participantFirstNames = [];
			this.participantLastNames = [];
			this.captcha.id = this.captchaId();
			this.captcha.value = '';
		},
		captchaId() {
			return `cf-${(Date.now().toString(36) + Math.random().toString(36).substring(2, 7))}`;
		},
    prefillAddress() {
      try {
        for (const key in this.form) {
          if (this.accountUserDetail && Object.hasOwn(this.accountUserDetail, key)) {
            this.form[key] = this.accountUserDetail[key]
          }
          if (this.accountLastAddress && Object.hasOwn(this.accountLastAddress, key)) {
            this.form[key] = this.accountLastAddress[key]
          }
        }
      } catch {}
		}
	},
	async created() {
    this.loading = true;

		try {
      await this.$store.dispatch('courses/loadCourseDates', { eventType: this.eventType });
      await this.$nextTick()

      const { eventId, eventRegion, eventDate } = getUrlParams()
      let event

      if (eventId) {
        event = this.events.find(event => event.id === +eventId)

        if (event) {
          this.form.eventId = event.id
        }
      }

      if (this.showRegionSelection) {
        if (event) {
          this.selectedRegionCode = event.location.region || 'none'
        } else {
          if (eventRegion && this.eventsByRegions.some(region => region.code === eventRegion)) {
            this.selectedRegionCode = eventRegion
          } else {
            this.selectedRegionCode = this.regionOptions[0]?.value
          }
        }
      }

      if (this.isBooking) {
        if (event) {
          this.selectedDate = switchDateFormat(event.date, '.')
        } else if (eventDate && this.availableEventDates.includes(eventDate)) {
          this.selectedDate = switchDateFormat(eventDate, '.')
        }

        const minDate = new Date(switchDateFormat(this.selectedDate || this.availableMinDate, '-'))
        this.onMonthChange({ month: minDate.getMonth() + 1, year: minDate.getFullYear()})
      }
		}
		catch(err) {
      this.isDebug() && console.error(err);
      if (err.isMaintenanceError) {
        this.onMaintenanceMode()
      }
    }
		this.loading = false;
	},
  mounted() {
    eventhub.$on(EVENT_ACCOUNT_ISLOGGEDIN, this.prefillAddress)

    if (this.isLoggedIn) {
      this.prefillAddress()
    }
  },
  destroyed() {
		eventhub.$off(EVENT_ACCOUNT_ISLOGGEDIN, this.prefillAddress)
	},
};
</script>

<style>
</style>
